<template>
  <div class="taxSettings">
    <SectionHeading class="mb-3 py-4" label="Tax Settings">
      <template #label>
        <h3 class="page-header-2">Tax Settings</h3>
      </template>
      <template v-if="settings && !isEditing" #actions>
        <sera-button
          v-if="!isEditing"
          data-cy="show-edit-tax-settings"
          icon="pencil"
          label="Edit Settings"
          @click="handleisEditing"
        />
      </template>
    </SectionHeading>
    <SimpleTable v-if="!isEditing" class="settingsTable">
      <thead>
        <tr>
          <td style="width: 120px"><strong>Taxability</strong></td>
          <td class="taxZone">
            <strong>Tax Zone</strong>
          </td>
          <td v-if="hasReason">
            <strong>{{ reasonLabel }}</strong>
          </td>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td data-cy="taxability-display">
            {{ friendlyTaxabilityDisplay(settings.taxability) }}
          </td>
          <td class="taxZone" data-cy="tax-zone-display">
            <div
              v-if="hasMissingTaxZone(settings)"
              class="sera-color-text-error"
            >
              <i class="fa fa-exclamation-circle"></i>
              Missing Tax Zone
            </div>
            <div v-else>
              {{ settings.tax_zone_formatted_name || "None" }}
            </div>
          </td>
          <td v-if="hasReason" class="reasonDisplay" data-cy="reason-display">
            {{ settings.reason }}
          </td>
        </tr>
      </tbody>
    </SimpleTable>
    <div v-else>
      <TaxSettingsForm
        ref="taxabilityForm"
        :context="context"
        :context-type="contextType"
        :data="settings"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";

import SectionHeading from "@/components/SectionHeading.vue";
import TaxSettingsForm from "@/Taxability/TaxSettingsForm.vue";
import {
  friendlyTaxabilityDisplay,
  hasMissingTaxZone,
  TaxSettingsContextTypes,
  TaxSettingsData
} from "@/typings/taxability";

const props = defineProps({
  isEditing: {
    type: Boolean,
    default: false
  },
  context: {
    type: [String, Number, Object],
    default: null,
    required: false
  },
  contextType: {
    type: String as () => TaxSettingsContextTypes,
    default: null,
    required: true
  },
  settings: {
    type: Object as () => TaxSettingsData,
    default: () => undefined,
    required: true
  }
});
const emit = defineEmits(["show-edit-tax-settings"]);
defineExpose({
  validate,
  getFormData
});

const taxabilityForm = ref<any>(null);

const hasReason = computed(() => {
  return !!props.settings?.reason;
});

const reasonLabel = computed(() => {
  if (props.settings?.taxability === "tax_exempt") {
    return "Reason for exemption";
  } else {
    return "Reason";
  }
});

function handleisEditing() {
  emit("show-edit-tax-settings");
}

async function validate() {
  return taxabilityForm.value.validate();
}

async function getFormData() {
  const valid = await validate();
  if (!valid) throw "Invalid form";
  return taxabilityForm.value.getFormData();
}
</script>

<style lang="scss" scoped>
.settingsTable {
  width: auto;
  :deep(table) {
    width: auto !important;
  }
  :deep(td) {
    white-space: wrap;
    padding-right: 30px;
    &.reasonDisplay {
      max-width: 280px;
    }
    &.taxZone {
      max-width: 250px;
      white-space: wrap;
      &.hasReason {
        max-width: 180px;
      }
    }
  }
}
</style>
